import { Components, Theme, ComponentsOverrides } from '@mui/material/styles';
import { Box, MenuList, MenuItem, ListItemText, Checkbox } from '@mui/material';
import { ChevronDownIcon } from '@/src/components/icon/stroke/ChevronDownIcon';
import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';
import { flowMenu } from '../menu/menu.theme';
import { flowMenuItem } from '../menu/menuItem.theme';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export const flowAutocomplete: Components<Theme>['MuiAutocomplete'] = {
  defaultProps: {
    // ensure clicking on AutoComplete sent event to FormControl to correctly handle FocusRing
    onPointerDown: (e) => {
      const formControlEl = e.currentTarget.querySelector('.MuiFormControl-root');
      // we have formControl and click is outside of it (clicking on margin from helperText)
      if (formControlEl && !formControlEl.contains(e.target as Node)) {
        // sent pointerDown event to it
        formControlEl.dispatchEvent(
          new PointerEvent('pointerdown', {
            bubbles: true,
            cancelable: true,
            pointerType: e.pointerType,
          }),
        );
      }
    },
    popupIcon: <ChevronDownIcon />,
    clearIcon: <CloseIcon />,
    noOptionsText: 'No matching results',
    slots: {
      // we are replacing listbox to remove Autocomplete styling and match with Menu design
      listbox: MenuList,
    },
    // option matched with design of menu with Checkbox for multiple choices
    renderOption: ({ key, ...rest }, option, state, ownerState) => {
      return (
        <MenuItem key={key} {...rest}>
          {ownerState?.multiple && <Checkbox checked={state.selected} tabIndex={-1} />}
          <ListItemText>{option?.label ?? option}</ListItemText>
        </MenuItem>
      );
    },
    // in @mui v7 renamed to renderValue, but it changes behavior for single value input
    renderTags: (value) =>
      (Array.isArray(value) ? value : [value]).map((v, idx) => (
        <Box key={v} component="span">
          {`${v}${(Array.isArray(value) ? value : [value]).length !== idx + 1 ? ', ' : ''}`}
        </Box>
      )),
  },
  styleOverrides: {
    root: ({ theme }) => ({
      // overwrite autoComplete default styling and match with TextField
      '& .MuiFilledInput-root': {
        // gap between tags and input
        gap: theme.spacing(0.5),
        // field proportions matched with textfield
        padding: '24px 12px 8px',
        // hidden label for variant with placeholder
        '&.MuiInputBase-hiddenLabel': {
          padding: '18px 12px 14px',
        },
        // small
        '&.MuiInputBase-sizeSmall': {
          padding: '22px 12px 4px',
          '&.MuiInputBase-hiddenLabel': {
            padding: '14px 12px 12px',
          },
        },
        // moved padding from input to allow wrap with too many tags
        '&, &.MuiInputBase-sizeSmall, &.MuiInputBase-hiddenLabel, &.MuiInputBase-sizeSmall.MuiInputBase-hiddenLabel': {
          '& .MuiFilledInput-input': {
            padding: '0',
          },
        },
      },
    }),
    // select arrow
    popupIndicator: {
      // remove default style
      transition: 'none',
      ...getInteractiveStyle(
        { color: 'inherit', backgroundColor: 'transparent' },
        { color: 'inherit', backgroundColor: 'transparent' },
      ),
      // style icon
      '& .MuiSvgIcon-root': {
        fontSize: '17px',
        flexShrink: '0',
      },
    },
    // 'x' clear button
    clearIndicator: {
      // keep always visible once the value is selected
      visibility: 'visible',
      // remove default style
      transition: 'none',
      ...getInteractiveStyle(
        { color: 'inherit', backgroundColor: 'transparent' },
        { color: 'inherit', backgroundColor: 'transparent' },
      ),
      // style icon
      '& .MuiSvgIcon-root': {
        fontSize: '17px',
        flexShrink: '0',
      },
      // select arrow next to it
      '& + .MuiAutocomplete-popupIndicator': {
        // hide it
        display: 'none',
      },
    },
    // chip tags, no longer used as we overwrite renderTags fn
    tag: {
      // space between multiple chips and limit tags text
      margin: '0',
      '&.MuiChip-root': {
        // reduce size
        padding: '0px 8px',
        '& .MuiChip-deleteIcon': {
          '&, &:last-of-type': {
            marginLeft: '2px',
          },
        },
      },
    },
    // select popover
    popper: {
      // direct style of listBox is not used, as it is overwritten via slots property to remove default styling
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        maxHeight: '240px',
        // necessary to make scroll with keyboard navigation work
        overflow: 'auto',
      },
    },
    // holder of list
    paper: flowMenu.styleOverrides.paper as ComponentsOverrides<Theme>['MuiAutocomplete'],
    // list options
    option: flowMenuItem.styleOverrides.root as ComponentsOverrides<Theme>['MuiAutocomplete'],
    // no matching result
    noOptions: [
      flowMenuItem.styleOverrides.root as ComponentsOverrides<Theme>['MuiAutocomplete'],
      {
        pointerEvents: 'none',
        cursor: 'auto',
      },
    ],
  },
};
